import { useEffect, useId, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';

type TextControl = HTMLInputElement | HTMLTextAreaElement;

/** Inserts a complete token over the current selection, then restores the
 * caret after React has applied the controlled value. Never truncates a token. */
export function useDynamicTextInsertion(
  target: RefObject<TextControl | null>, value: string, onChange: (value: string) => void, maxLength?: number,
) {
  const pendingCaret = useRef<number | null>(null);
  useLayoutEffect(() => {
    const node = target.current;
    if (!node || pendingCaret.current === null) return;
    const caret = pendingCaret.current;
    pendingCaret.current = null;
    node.focus();
    node.setSelectionRange(caret, caret);
  });
  return (token: string): boolean => {
    const node = target.current;
    if (!node || node.readOnly || node.matches(':disabled')) return false;
    const start = node.selectionStart ?? value.length;
    const end = node.selectionEnd ?? start;
    const next = value.slice(0, start) + token + value.slice(end);
    if (maxLength !== undefined && next.length > maxLength) return false;
    pendingCaret.current = start + token.length;
    onChange(next);
    return true;
  };
}

const FIELDS = [
  { key: 'first_name', label: 'First name', fallback: 'there' },
  { key: 'name', label: 'Full name', fallback: 'your guest' },
  { key: 'email', label: 'Email address', fallback: 'your email address' },
  { key: 'title', label: 'Appointment title', fallback: 'your appointment' },
  { key: 'when', label: 'Appointment date and time', fallback: 'the scheduled time' },
  { key: 'date', label: 'Appointment date', fallback: 'the scheduled date' },
  { key: 'time', label: 'Appointment time', fallback: 'the scheduled time' },
  { key: 'duration', label: 'Appointment duration', fallback: 'the scheduled duration' },
] as const;
const CONTROL_KEYS = new Set(['pt', 'step']);
const INPUT = 'input h-8 min-w-0 rounded-lg py-0 text-sm';

/** A small, explicitly enabled control for visible page copy. URL fields and
 * configuration fields never mount it. Merely inserts text into the editor;
 * it does not add contact data to links, storage or requests. */
export function DynamicTextPicker({ label, disabled, onInsert }: {
  label: string; disabled?: boolean; onInsert: (token: string) => boolean;
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [field, setField] = useState('first_name');
  const [customKey, setCustomKey] = useState('');
  const [fallback, setFallback] = useState('there');
  const [error, setError] = useState('');
  const wrap = useRef<HTMLSpanElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const select = useRef<HTMLSelectElement>(null);
  const [position, setPosition] = useState({ left: 16, top: 16, width: 260, maxHeight: 480 });
  const key = field === 'custom' ? customKey.trim().toLowerCase() : field;
  const validKey = /^[a-z][a-z0-9_.:-]{0,79}$/.test(key) && !CONTROL_KEYS.has(key);
  const cleanFallback = fallback.replace(/[\r\n]+/g, ' ');
  const validFallback = !/[{}]/.test(cleanFallback);
  const token = `{{${key}${cleanFallback ? `|${cleanFallback}` : ''}}}`;

  // A portal keeps the picker out of clipped inspector rows. Clamp it to the
  // viewport so fields in the first column work on narrow screens too.
  function place() {
    const rect = trigger.current?.getBoundingClientRect();
    if (!rect || !panel.current) return;
    const width = Math.min(260, window.innerWidth - 32);
    const maxHeight = window.innerHeight - 32;
    const height = Math.min(panel.current.scrollHeight, maxHeight);
    const next = {
      left: Math.max(16, Math.min(rect.right - width, window.innerWidth - width - 16)),
      top: Math.max(16, Math.min(rect.bottom + 6, window.innerHeight - height - 16)), width, maxHeight,
    };
    setPosition(previous => Object.entries(next).every(([key, value]) => previous[key as keyof typeof next] === value) ? previous : next);
  }
  useLayoutEffect(() => { if (open) place(); });

  useEffect(() => {
    if (!open) return;
    select.current?.focus();
    const outside = (event: PointerEvent) => {
      if (!wrap.current?.contains(event.target as Node) && !panel.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', outside);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      document.removeEventListener('pointerdown', outside);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);
  useEffect(() => { if (disabled) setOpen(false); }, [disabled]);

  function close() { setOpen(false); trigger.current?.focus(); }
  function insert() {
    if (disabled || !validKey || !validFallback) return;
    if (!onInsert(token)) { setError('This token does not fit. Shorten the text or select text to replace.'); return; }
    setError('');
    setOpen(false);
  }

  return <span ref={wrap} className="relative inline-flex shrink-0">
    <button ref={trigger} type="button" disabled={disabled} aria-label={`Insert dynamic text into ${label}`}
      title="Insert dynamic text" aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? id : undefined}
      className="inline-grid h-6 w-7 place-items-center rounded text-xs font-medium text-textDim hover:bg-text/[0.04] hover:text-text disabled:pointer-events-none disabled:opacity-40 [@media(pointer:coarse)]:h-9 [@media(pointer:coarse)]:w-9"
      onMouseDown={event => event.preventDefault()}
      onClick={() => { setError(''); setOpen(!open); }}><span aria-hidden>{'{ }'}</span></button>
    {open && createPortal(<div className="pe-scope"><div ref={panel} id={id} role="dialog" aria-label={`Dynamic text for ${label}`} style={position}
      className="fixed z-[100] grid gap-3 overflow-y-auto rounded-lg border border-border bg-surface p-3 text-sm text-text shadow-lg"
      onKeyDown={event => {
        if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(); }
        if (event.key === 'Enter' && event.target instanceof HTMLInputElement) { event.preventDefault(); insert(); }
      }}>
      <span className="flex items-center justify-between gap-2"><span className="font-semibold">Insert dynamic text</span>
        <button type="button" aria-label="Close dynamic text" className="rounded px-1.5 py-0.5 text-textDim hover:bg-text/[0.04]" onClick={close}>×</button></span>
      <label className="grid gap-1 text-xs font-medium">Field
        <select ref={select} className="select h-8 min-w-0 rounded-lg py-0 text-sm" value={field}
          onChange={event => {
            const next = event.target.value;
            setField(next); setFallback(FIELDS.find(item => item.key === next)?.fallback ?? ''); setError('');
          }}>
          {FIELDS.map(item => <option key={item.key} value={item.key}>{item.label}</option>)}
          <option value="custom">Custom URL field</option>
        </select>
      </label>
      {field === 'custom' && <label className="grid gap-1 text-xs font-medium">URL field name
        <input className={INPUT} value={customKey} placeholder="city" spellCheck={false} maxLength={80}
          aria-describedby={`${id}-source`} aria-invalid={customKey && !validKey ? true : undefined}
          onChange={event => { setCustomKey(event.target.value); setError(''); }} />
      </label>}
      <label className="grid gap-1 text-xs font-medium">Fallback text
        <input className={INPUT} value={fallback} placeholder="Shown when the field is empty" maxLength={120}
          aria-invalid={!validFallback || undefined} onChange={event => { setFallback(event.target.value); setError(''); }} />
      </label>
      <span id={`${id}-source`} className="text-xs font-normal leading-relaxed text-textDim">Uses values supplied in this page’s URL. If missing, the fallback appears. No contact data is added automatically.</span>
      {field === 'custom' && customKey && !validKey && <span role="alert" className="text-xs text-danger">Use a field name such as city or utm_source. Preview and step fields are reserved.</span>}
      {!validFallback && <span role="alert" className="text-xs text-danger">Fallback text cannot contain braces.</span>}
      {error && <span role="alert" className="text-xs text-danger">{error}</span>}
      {validKey && validFallback && <code className="min-w-0 break-words rounded bg-text/[0.04] px-2 py-1 text-xs">{token}</code>}
      <button type="button" disabled={disabled || !validKey || !validFallback} className="btn-secondary h-8 rounded-lg py-0" onClick={insert}>Insert</button>
    </div></div>, document.body)}
  </span>;
}
